import React from 'react';
import store from '../../../store';
import {
  TErrorValidation,
  TSuccessfulValidation,
  previousValidationActions,
} from '../../../store/previousValidationSlice';
import { ValidationContext } from './ValidationContext';

/**
 * Este hook devuelve una función que al ser invocada
 * intentará validar el formulario actual. La misma
 * devuelve { submitValues: FormType } en caso de que la
 * validación sea exitosa o false en caso contrario.
 *
 * El proceso de validación es en realidad más amplio
 * que este hook, el mismo integra también el hook
 * useCustomField para implementar fields personalizados.
 *
 * Una forma sencilla de saber si la validación tuvo
 * éxito es utilizar el método hasSucceedValidation.
 *
 * @see hasSucceedValidation
 * @see useCustomField
 * @see ValidationContextProvider
 * @see TValidate
 * @see validation/Input
 * @see validation/Checkbox
 * @see validation/Select
 * @see validation/Textarea
 *
 * @returns validate - Un método que ejecuta la validación
 * del formulario. En caso de validar correctamente, devuelve
 * un objeto con los submitValues o en caso contrario un
 * objeto con los errores.
 *
 * @example
 *
 * # Obteniendo un contexto de validación
 *
 * <ValidationContext form="DatosPersonales">{children}</ValidationContext>
 *
 * @example
 *
 * # Ejecutando una validación
 *
 * const validate = useValidation();
 * ...
 * submitHandler = React.useCallback(async ()=>{
 *   const validationResult = await validate();
 *
 *   if(validationResult !== false) {
 *     const postResult = await ApiaApi.post('url', { postData:
 *     validationResult.submitValues }); ...
 *   }
 * },[])
 *
 */
export default function useValidation<
  FormType extends Record<string, unknown> = Record<string, unknown>,
>(customForm?: string) {
  const context = React.useContext(ValidationContext);

  if (!context && !customForm)
    throw new Error(
      'No se definió ningún contexto de validación. Este hook debe ser usado bien dentro de un ValidationContext o con un parámetro customForm definido.',
    );

  const validate = React.useCallback(async () => {
    const result = await store
      .dispatch(
        previousValidationActions.validate({
          form: customForm ?? context?.form ?? 'TYPESCRIPT_LOL',
        }),
      )
      .unwrap();

    return result as
      | TSuccessfulValidation<FormType>
      | TErrorValidation<FormType>;
  }, [context?.form, customForm]);

  return validate;
}
